<template>
  <button
    class="border flex items-center justify-between gap-2 relative overflow-hidden"
    :class="{
      'min-h-9 px-3 py-2': size !== 'lg',
      'min-h-12 px-4 py-3': size === 'lg',
      'rounded-2xl': newLayout,
      'rounded-md': !newLayout,
      'border-border bg-surface-secondary': state === 'NORMAL' && newLayout,
      'border-brand-bgdivider-10 bg-brand-bgdivider-20':
        state === 'NORMAL' && !newLayout,
      'hover:border-brand-secondary-80 hover:bg-brand-quaternary-40 cursor-pointer':
        state === 'NORMAL' && !props.readOnly,
      'border-brand-secondary-80 bg-brand-primary':
        state === 'SUBMITTED' && !isPoll,
      'border-brand-secondary-80': state === 'SUBMITTED' && isPoll,
      'border-brand-bgdivider-10 bg-basics-accent-green ': state === 'CORRECT',
    }"
  >
    <div
      v-if="isPoll && enableParticipationCount"
      class="absolute inset-0 bg-fill-brand transition-all duration-500 ease-out flex justify-between items-center z-0"
      :style="{ width: `${percentage}%` }"
    ></div>

    <span
      class="flex-1 min-w-0 text-left line-clamp-1 break-good relative"
      :class="[
        size === 'lg' ? 'text-base leading-6' : 'text-sm leading-5',
        newLayout && state === 'NORMAL' ? 'text-text' : '',
      ]"
      >{{ optionLabel }}</span
    >

    <span
      v-if="isPoll"
      class="flex w-10 flex-shrink-0 items-center justify-end relative"
      :class="{ invisible: !enableParticipationCount }"
      :aria-hidden="!enableParticipationCount"
    >
      <span class="text-[13px] font-serif text-text">{{ percentage }}%</span>
    </span>
  </button>
</template>

<script setup lang="ts">
import type { QuizQuestionOption } from "@/generated/graphql";
import { useObjectTranslation } from "@/stores/translation.store";
import { useFeatureFlags } from "@/composables/use-feature-flags";
import { computed } from "vue";

const { getBoolean } = useFeatureFlags();
const newLayout = getBoolean("enable_new_channel_layout");

const props = defineProps<{
  createdById: string | null | undefined;
  option: QuizQuestionOption;
  enableParticipationCount: boolean;
  questionParticipationCount: number;
  questionOptionParticipationCount: number;
  isSubmitted: boolean;
  isCorrect: boolean;
  isPoll: boolean;
  readOnly: boolean;
  size?: "md" | "lg";
}>();

const state = computed(() => {
  if (props.isCorrect) return "CORRECT";
  if (props.isSubmitted) return "SUBMITTED";
  return "NORMAL";
});

const { text: optionLabel } = useObjectTranslation(
  computed(() => ({
    authorUserId: props.createdById,
    originalText: props.option.label,
    objectAccessors: { objectId: props.option.id, objectProperty: "label" },
    translatedText: props.option.quiz_question_option_translations[0]?.label,
  })),
);

const percentage = computed(() => {
  const total = props.questionParticipationCount;
  if (total === 0) return 0;
  return Math.round((props.questionOptionParticipationCount / total) * 100);
});
</script>
